import { EntityAppearanceType } from '@/api/generated/api.enum';
import { updateSchoolClassAppearance, uploadSchoolClassImage } from '@/api/resources/entityAppearance.api';
import type { Components } from '@/api/generated/api';
import { useToast } from '@/composables/useToast';
import { appearanceFromDto, appearanceToDto, appearancesEqual } from '@/core/entity-appearance/appearanceFromDto';
import type { TEntityAppearance } from '@/core/entity-appearance/entityAppearance.types';
import { useCurrentClassStore } from '@/stores/useCurrentClassStore';
import { useQueryClient } from '@tanstack/vue-query';
import { computed, reactive, type Ref } from 'vue';
import { schoolClassKeys } from './schoolClass.keys';

interface ISchoolClassAppearanceOwner {
  id?: number;
  appearance?: Components.Schemas.EntityAppearanceDto | null;
}

interface ISchoolClassAppearanceChange {
  source: ISchoolClassAppearanceOwner['appearance'];
  appearance: TEntityAppearance;
  saving: boolean;
}

const toSavePayload = (appearance: TEntityAppearance) => {
  if (appearance.type !== EntityAppearanceType.image) return appearanceToDto(appearance);
  // Existing image URLs are display values; uploads require a newly selected File.
  return appearance.source instanceof File ? { file: appearance.source } : null;
};

export const useSchoolClassAppearance = (owner: Readonly<Ref<ISchoolClassAppearanceOwner | null>>) => {
  const queryClient = useQueryClient();
  const currentClassStore = useCurrentClassStore();
  const { showError } = useToast();
  const changes = reactive(new Map<number, ISchoolClassAppearanceChange>());
  const currentChange = computed(() => {
    const id = owner.value?.id;
    const change = id == null ? undefined : changes.get(id);
    // Only local saves override owner data; refreshed data takes over once a save finishes.
    return change?.saving || change?.source === owner.value?.appearance ? change : undefined;
  });
  const appearance = computed(() => currentChange.value?.appearance ?? appearanceFromDto(owner.value?.appearance));
  const saving = computed(() => currentChange.value?.saving ?? false);

  const updateCachedAppearance = (id: number, saved: Components.Schemas.EntityAppearanceDto | null): void => {
    queryClient.setQueriesData<Components.Schemas.SchoolClassDto[]>({ queryKey: schoolClassKeys.lists() }, (classes) =>
      classes?.map((schoolClass) => (schoolClass.id === id ? { ...schoolClass, appearance: saved } : schoolClass)),
    );
    if (currentClassStore.currentClass?.id === id) {
      currentClassStore.updateCurrentClass({ ...currentClassStore.currentClass, appearance: saved });
    }
    void queryClient.invalidateQueries({ queryKey: schoolClassKeys.all });
  };

  const save = async (next: TEntityAppearance): Promise<boolean> => {
    const id = owner.value?.id;
    if (id == null || saving.value) return false;
    if (appearancesEqual(appearance.value, next)) return true;
    const payload = toSavePayload(next);
    if (!payload) return false;

    const previous = currentChange.value;
    const change = reactive({ source: owner.value?.appearance, appearance: next, saving: true });
    changes.set(id, change);
    try {
      await queryClient.cancelQueries({ queryKey: schoolClassKeys.lists() });
      const schoolClass =
        'file' in payload
          ? await uploadSchoolClassImage({ schoolClassId: id, file: payload.file })
          : await updateSchoolClassAppearance({ schoolClassId: id, dto: payload });
      change.appearance = appearanceFromDto(schoolClass.appearance);
      updateCachedAppearance(id, schoolClass.appearance);
      return true;
    } catch (error) {
      if (previous) changes.set(id, previous);
      else changes.delete(id);
      showError(error);
      return false;
    } finally {
      change.saving = false;
    }
  };

  return {
    appearance,
    saving,
    save,
  };
};
